import { Vector3 } from 'three';

import type { GeoLightingManager } from '../geo-lighting-manager.class';
import type { CelestialPosition, TimePreset } from '../../../types';
import { DEG_TO_RAD, RAD_TO_DEG, TIME_PRESET_ELEVATIONS } from '../helpers';

/**
 * Convert time preset to a Date object
 */
export const getTimeFromPresetCfn = (preset: TimePreset): Date => {
	const now = new Date();
	const year = now.getFullYear();
	const month = now.getMonth();
	const day = now.getDate();

	// Approximate times for presets
	const presetHours: Record<TimePreset, number> = {
		sunrise: 6,
		morning: 9,
		afternoon: 14,
		evening: 17,
		sunset: 19,
		night: 22
	};

	const hours = presetHours[preset];
	return new Date(year, month, day, hours, 0, 0);
};

/**
 * Get the current effective time
 */
export const getCurrentTimeCfn = (
	self: GeoLightingManager
): Date => {
	const time = self.time;
	if (time === 'realtime') {
		return new Date();
	}
	if (time instanceof Date) {
		return time;
	}
	// Time preset - use current date with appropriate time
	return getTimeFromPresetCfn(time);
};

export interface CalculateSunPositionParams {
	date: Date;
}

/**
 * Calculate sun position using simplified solar equations
 * Based on NOAA solar calculations (simplified)
 */
export const calculateSunPositionCfn = (
	self: GeoLightingManager,
	{ date }: CalculateSunPositionParams
): CelestialPosition => {
	const { latitude, longitude } = self.location;
	const time = self.time;

	// If using a time preset, use the preset elevations directly
	if (typeof time === 'string' && time !== 'realtime') {
		const preset = TIME_PRESET_ELEVATIONS[time as TimePreset];
		if (preset) {
			return {
				azimuth: 180 + preset.azimuthOffset, // South-based
				elevation: preset.elevation
			};
		}
	}

	// Day of year (1-366)
	const start = new Date(date.getFullYear(), 0, 0);
	const diff = date.getTime() - start.getTime();
	const oneDay = 1000 * 60 * 60 * 24;
	const dayOfYear = Math.floor(diff / oneDay);

	// Solar declination (simplified)
	const declination = 23.45 * Math.sin(DEG_TO_RAD * (360 / 365) * (dayOfYear - 81));

	// Hour angle
	const hours = date.getHours() + date.getMinutes() / 60 + date.getSeconds() / 3600;
	const solarNoon = 12 - longitude / 15; // Approximate solar noon
	const hourAngle = 15 * (hours - solarNoon);

	// Convert to radians
	const latRad = latitude * DEG_TO_RAD;
	const declRad = declination * DEG_TO_RAD;
	const hourAngleRad = hourAngle * DEG_TO_RAD;

	// Calculate elevation (altitude)
	const sinElevation =
		Math.sin(latRad) * Math.sin(declRad) +
		Math.cos(latRad) * Math.cos(declRad) * Math.cos(hourAngleRad);
	const elevation = Math.asin(sinElevation) * RAD_TO_DEG;

	// Calculate azimuth
	const cosAzimuth =
		(Math.sin(declRad) - Math.sin(latRad) * sinElevation) /
		(Math.cos(latRad) * Math.cos(elevation * DEG_TO_RAD));

	let azimuth = Math.acos(Math.max(-1, Math.min(1, cosAzimuth))) * RAD_TO_DEG;

	// Correct azimuth quadrant
	if (hourAngle > 0) {
		azimuth = 360 - azimuth;
	}

	return { azimuth, elevation };
};

export interface CalculateMoonPositionParams {
	date: Date;
}

/**
 * Calculate moon position (simplified - opposite of sun with offset)
 */
export const calculateMoonPositionCfn = (
	self: GeoLightingManager,
	{ date }: CalculateMoonPositionParams
): CelestialPosition => {
	const sunPos = calculateSunPositionCfn(self, { date });

	// Simplified: moon is roughly opposite the sun with some variation
	// In reality, moon position depends on lunar phase and orbit
	const moonAzimuth = (sunPos.azimuth + 180) % 360;

	// Moon elevation when sun is below horizon
	// Simple approximation: inverse of sun elevation, capped
	const moonElevation = Math.max(10, Math.min(60, -sunPos.elevation + 30));

	return {
		azimuth: moonAzimuth,
		elevation: moonElevation
	};
};

/**
 * Convert celestial position to 3D light position
 */
export const celestialToPositionCfn = (
	self: GeoLightingManager,
	position: CelestialPosition
): Vector3 => {
	const azimuthRad = position.azimuth * DEG_TO_RAD;
	const elevationRad = position.elevation * DEG_TO_RAD;
	const { lightDistance, sceneCenter } = self.internalAccess;

	// Convert spherical to Cartesian
	// Azimuth: 0 = North (+Z), 90 = East (+X), 180 = South (-Z), 270 = West (-X)
	const cosElevation = Math.cos(elevationRad);
	const x = lightDistance * cosElevation * Math.sin(azimuthRad);
	const y = lightDistance * Math.sin(elevationRad);
	const z = lightDistance * cosElevation * Math.cos(azimuthRad);

	return new Vector3(
		sceneCenter.x + x,
		Math.max(10, sceneCenter.y + y), // Keep light above ground
		sceneCenter.z + z
	);
};
